import React, { useState, useMemo } from 'react';
import styles from './Table.module.css';

export interface TableColumn<T> {
  key: keyof T;
  label: string;
  width?: string;
  sortable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
}

interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  keyField: keyof T;
  isLoading?: boolean;
  isEmpty?: boolean;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  rowClassName?: (row: T) => string;
  sortBy?: keyof T;
  sortOrder?: 'asc' | 'desc';
  onSort?: (key: keyof T, order: 'asc' | 'desc') => void;
}

export const Table = React.forwardRef<HTMLTableElement, TableProps<any>>(
  (
    {
      columns,
      data,
      keyField,
      isLoading = false,
      isEmpty = false,
      emptyMessage = 'No data available',
      onRowClick,
      rowClassName,
      sortBy,
      sortOrder = 'asc',
      onSort,
    },
    ref
  ) => {
    const [internalSortBy, setInternalSortBy] = useState<keyof any | null>(sortBy || null);
    const [internalSortOrder, setInternalSortOrder] = useState<'asc' | 'desc'>(sortOrder);

    const handleSort = (key: keyof any) => {
      if (!columns.find((col) => col.key === key)?.sortable) return;

      let newOrder: 'asc' | 'desc' = 'asc';
      if (internalSortBy === key && internalSortOrder === 'asc') {
        newOrder = 'desc';
      }

      setInternalSortBy(key);
      setInternalSortOrder(newOrder);

      if (onSort) {
        onSort(key, newOrder);
      }
    };

    const sortedData = useMemo(() => {
      if (!internalSortBy || isLoading || isEmpty) return data;

      return [...data].sort((a, b) => {
        const aVal = a[internalSortBy];
        const bVal = b[internalSortBy];

        if (aVal === null || aVal === undefined) return 1;
        if (bVal === null || bVal === undefined) return -1;

        if (typeof aVal === 'string') {
          return internalSortOrder === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        if (typeof aVal === 'number') {
          return internalSortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        }

        return 0;
      });
    }, [data, internalSortBy, internalSortOrder, isLoading, isEmpty]);

    if (isLoading) {
      return (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <p>Loading...</p>
        </div>
      );
    }

    if (isEmpty || data.length === 0) {
      return (
        <div className={styles.emptyContainer}>
          <p>{emptyMessage}</p>
        </div>
      );
    }

    return (
      <div className={styles.tableWrapper}>
        <table className={styles.table} ref={ref}>
          <thead className={styles.thead}>
            <tr>
              {columns.map((column) => (
                <th
                  key={String(column.key)}
                  style={{ width: column.width }}
                  className={column.sortable ? styles.sortable : ''}
                  onClick={() => column.sortable && handleSort(column.key)}
                >
                  <div className={styles.headerContent}>
                    <span>{column.label}</span>
                    {column.sortable && internalSortBy === column.key && (
                      <span className={`${styles.sortIcon} ${styles[internalSortOrder]}`}>
                        {internalSortOrder === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={styles.tbody}>
            {sortedData.map((row) => (
              <tr
                key={String(row[keyField])}
                className={`${styles.row} ${rowClassName ? rowClassName(row) : ''} ${
                  onRowClick ? styles.clickable : ''
                }`}
                onClick={() => onRowClick && onRowClick(row)}
              >
                {columns.map((column) => (
                  <td key={`${String(row[keyField])}-${String(column.key)}`}>
                    {column.render ? column.render(row[column.key], row) : row[column.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
);

Table.displayName = 'Table';
